import { useT } from '@/i18n';
import { todayString } from '@/utils/datetime';
import { Popover, f7 } from 'framework7-react';
import { DateTime } from 'luxon';
import { type MutableRefObject, useEffect, useRef, useState } from 'react';

// Matches the F7-react Popover ref shape (mirrors RestaurantSwitcherSheet).
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type PopoverInstance = { el: HTMLElement | null; f7Popover: () => any };

interface Props {
  open: boolean;
  onClose: () => void;
  /** Element the popover anchors to (the date-strip calendar button). */
  anchorEl: HTMLElement | null;
  /** Selected date as YYYY-MM-DD (restaurant local timezone). */
  selectedDate: string;
  onSelect: (date: string) => void;
  /** Optional earliest selectable date (YYYY-MM-DD). Earlier days render dimmed
   *  and non-tappable, and the prev-month nav stops at its month. Omit for no
   *  lower bound (the default - every day stays selectable). */
  minDate?: string;
  /** Optional latest selectable date (YYYY-MM-DD). Later days render dimmed and
   *  non-tappable, and the next-month nav stops at its month. Omit for no upper
   *  bound (the default - every day stays selectable). */
  maxDate?: string;
}

// Resolve the month to view from an ISO date, tolerating an empty/invalid value
// (e.g. the edit form before its reservation date has loaded). An invalid input
// would make `viewMonth` an Invalid DateTime - every cell then renders "NaN" and
// its null ISO slips past the min/max guards, so disabled days become tappable.
// Fall back to the current month to keep the grid valid until a real date lands.
const monthOf = (iso: string) => {
  const dt = DateTime.fromISO(iso);
  return (dt.isValid ? dt : DateTime.now()).startOf('month');
};

/**
 * iOS-style inline month calendar shown in a popover anchored to its trigger -
 * the expanded form of Apple's compact date picker (HIG → Pickers). A bordered
 * card with a "Month YYYY" header + chevron nav, a Monday-first weekday row, and
 * a 6-week grid: today reads in the accent colour, the selected day is a filled
 * accent circle. Picking a day selects + closes.
 */
export default function MonthCalendarPopover({
  open,
  onClose,
  anchorEl,
  selectedDate,
  onSelect,
  minDate,
  maxDate,
}: Props) {
  const t = useT();
  const ref = useRef<PopoverInstance>(null);
  const [viewMonth, setViewMonth] = useState(() => monthOf(selectedDate));

  // Read the latest selectedDate at open-time WITHOUT depending on it: the date
  // often loads async, and re-running the open effect on every selectedDate change re-fired
  // f7.popover.open mid-animation → the popover repositioned/jumped months = jank.
  const selectedRef = useRef(selectedDate);
  selectedRef.current = selectedDate;

  // Drive the F7 popover from the controlled `open` prop, anchored to the
  // button. Re-sync the visible month to the selected date once, on each open.
  useEffect(() => {
    const el = ref.current?.el;
    if (!el) return;
    if (open && anchorEl) {
      setViewMonth(monthOf(selectedRef.current));
      f7.popover.open(el, anchorEl);
    }
    if (!open) f7.popover.close(el);
  }, [open, anchorEl]);

  const today = todayString();

  // Monday-first 6-week grid (42 cells) covering the viewed month. luxon weekday
  // is 1=Mon..7=Sun, so `(weekday + 6) % 7` is the offset back to the Monday.
  const gridStart = viewMonth.minus({ days: (viewMonth.weekday + 6) % 7 });
  const cells = Array.from({ length: 42 }, (_, i) => gridStart.plus({ days: i }));
  const weekdays = Array.from({ length: 7 }, (_, i) =>
    gridStart.plus({ days: i }).toFormat('ccccc'),
  );

  // With a lower bound, stop the prev-month nav at minDate's month.
  const canPrev = !minDate || viewMonth > monthOf(minDate);
  // With an upper bound, stop the next-month nav at maxDate's month.
  const canNext = !maxDate || viewMonth < monthOf(maxDate);

  const pick = (iso: string) => {
    onSelect(iso);
    f7.popover.close();
  };

  return (
    <Popover
      ref={ref as MutableRefObject<PopoverInstance>}
      className="rsv-cal-pop"
      style={{ width: 300 }}
      onPopoverClosed={onClose}
    >
      <div className="rsv-cal">
        <div className="rsv-cal-header">
          <div className="rsv-cal-title">{viewMonth.toFormat('LLLL yyyy')}</div>
          <div className="rsv-cal-nav">
            <button
              type="button"
              aria-label={t.prevMonth}
              disabled={!canPrev}
              onClick={() => canPrev && setViewMonth((m) => m.minus({ months: 1 }))}
            >
              <i className="f7-icons">chevron_left</i>
            </button>
            <button
              type="button"
              aria-label={t.nextMonth}
              disabled={!canNext}
              onClick={() => canNext && setViewMonth((m) => m.plus({ months: 1 }))}
            >
              <i className="f7-icons">chevron_right</i>
            </button>
          </div>
        </div>

        <div className="rsv-cal-grid rsv-cal-weekdays">
          {weekdays.map((w, i) => (
            <div key={i} className="rsv-cal-weekday">
              {w}
            </div>
          ))}
        </div>

        <div className="rsv-cal-grid">
          {cells.map((c) => {
            const iso = c.toISODate()!;
            const out = c.month !== viewMonth.month;
            const isToday = iso === today;
            const isSelected = iso === selectedDate;
            const disabled = (minDate ? iso < minDate : false) || (maxDate ? iso > maxDate : false);
            return (
              <button
                key={iso}
                type="button"
                disabled={disabled}
                className={`rsv-cal-day no-ripple${out ? ' rsv-cal-day--out' : ''}${
                  isToday ? ' rsv-cal-day--today' : ''
                }${isSelected ? ' rsv-cal-day--selected' : ''}${
                  disabled ? ' rsv-cal-day--disabled' : ''
                }`}
                onClick={() => !disabled && pick(iso)}
              >
                {c.day}
              </button>
            );
          })}
        </div>
      </div>
    </Popover>
  );
}
